import styled from 'styled-components';
import { button, colors } from 'ui/theme';
import { getFirstChar } from 'utils/stringHelpers';
import { COLOR_STYLES, SIZE_STYLES } from './Avatar.styles';
import { AVATAR_COLORS, AVATAR_SIZES } from './constants';

export type AVATAR_COLOR_KEYS = keyof typeof AVATAR_COLORS;
export type AVATAR_COLOR_TYPES = (typeof AVATAR_COLORS)[AVATAR_COLOR_KEYS];

export type AVATAR_SIZE_KEYS = keyof typeof AVATAR_SIZES;
export type AVATAR_SIZE_TYPES = (typeof AVATAR_SIZES)[AVATAR_SIZE_KEYS];

// https://zeroheight.com/3fe730a39/p/71e1a7-avatars
export type AvatarProps = {
  /** The background color of the Avatar (optional, will randomly assign if absent) */
  color?: AVATAR_COLOR_TYPES;
  /** First name of the user */
  firstName: string;
  /** Last name of the user (optional)  */
  lastName?: string;
  /** The image to display (optional) */
  imgSrc?: string;
  /** The size of the Avatar */
  size?: AVATAR_SIZE_TYPES;
  /** Svg to render in the Avatar
   * Do not provide and imgSrc if you want children to render.
   */
  children?: React.ReactNode;
  /** other pass through props */
  [key: string]: unknown;
};

export const BaseAvatar = styled.div<Pick<AvatarProps, 'color' | 'imgSrc' | 'size'>>`
  ${button}
  align-items: center;
  border-radius: 50%;
  color: ${({ color }) => (color ? colors.white : colors.secondaryText)};
  display: flex;
  line-height: 100%;
  justify-content: space-around;

  ${({ size }) => {
    if (size) {
      return SIZE_STYLES[size];
    }
    return '';
  }};

  background-color: ${({ color }) => (color ? COLOR_STYLES[color] : colors.grey15)};

  ${({ imgSrc }) =>
    imgSrc
      ? `
    background-size: cover;
    background-repeat: no-repeat;
    background-image: url("${imgSrc}");`
      : ``}
`;

export const Avatar = ({
  size = AVATAR_SIZES.SMALL,
  color,
  firstName = 'I',
  lastName = '',
  imgSrc,
  children,
  ...props
}: AvatarProps) => {
  const shouldRenderInitial = !imgSrc && !children;
  const firstInitial = getFirstChar(firstName);
  const lastInitial = getFirstChar(lastName);
  const colorValues = Object.values(AVATAR_COLORS);

  // randomize the background color if one is not explicitly provided
  const defaultColor = colorValues[(firstName + lastName).length % colorValues.length];

  return (
    <BaseAvatar size={size} color={color || defaultColor} imgSrc={imgSrc} data-private {...props}>
      {!imgSrc && children && children}
      {shouldRenderInitial ? firstInitial : ''}
      {shouldRenderInitial && lastInitial ? lastInitial : ''}
    </BaseAvatar>
  );
};

export type CountAvatarProps = {
  /** Number to display */
  count: string | number;
  /** The size of the Avatar */
  size?: AVATAR_SIZE_TYPES;
  /** other pass through props */
  [key: string]: unknown;
};

export const CountAvatar = ({ size = AVATAR_SIZES.SMALL, count }: CountAvatarProps) => (
  <>{count && <BaseAvatar size={size}>+{count}</BaseAvatar>}</>
);
